<template>
    <section
        :aria-labelledby="headingId"
        class="operations-insights-grid-section"
    >
        <div class="grid-header">
            <h3 :id="headingId">
                {{ title }}
            </h3>
            <OperationsInsightsMenu
                :disabled="!!error"
                @export-c-s-v="exportToCSV"
            />
        </div>
        <div
            v-if="error"
            class="error"
        >
            {{ error }}
        </div>
        <ag-grid-vue
            ref="gridRef"
            class="ag-theme-alpine grid"
            :column-defs="columnDefs"
            :row-data="rowData"
            :dom-layout="domLayout"
            :modules="modules"
            role="table"
            :aria-label="`${title} data table`"
            :pagination="true"
            :pagination-page-size="5"
            :pagination-page-size-selector="[5, 10]"
            :floating-filter="true"
            @grid-ready="onGridReady"
            @grid-destroyed="onGridDestroyed"
            :default-col-def="defaultColDef"
        />
    </section>
</template>

<script setup lang="ts">
// :pagination-page-size-selector="[5, 10, 25, 50, 100]"

// took out ag-theme-alpine from <ag-grid-vue class
// ag-theme-material
import { computed, ref } from 'vue';
import { AgGridVue } from '@ag-grid-community/vue3';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { CsvExportModule } from '@ag-grid-community/csv-export';
import { Module } from '@ag-grid-community/core';
import OperationsInsightsMenu from './OperationsInsightsMenu.vue';

interface ExtendedAgGridVue extends InstanceType<typeof AgGridVue> {
    _resizeCleanup?: () => void;
}

// import '@ag-grid-community/styles/ag-theme-material.css';

interface Props {
    title: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columnDefs: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    rowData: any[];
    error?: string | null;
}

const props = defineProps<Props>();

const gridRef = ref();
const modules = [ ClientSideRowModelModule, CsvExportModule ] as Module[];
const domLayout = 'autoHeight';
const headingId = computed(() => `${props.title.toLowerCase().replace(/\s+/g, '-')}-heading`);

const defaultColDef = {
    resizable: true,
    sortable: true,
    filter: true
};

const onGridReady = () => {
    gridRef.value?.api?.sizeColumnsToFit();
    
    // Add resize listener to handle browser window resize
    const handleResize = () => {
        gridRef.value?.api?.sizeColumnsToFit();
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup on component unmount
    const cleanup = () => {
        window.removeEventListener('resize', handleResize);
    };
    
    // Store cleanup function for later use
    (gridRef.value as ExtendedAgGridVue)._resizeCleanup = cleanup;
};

const onGridDestroyed = () => {
    // Clean up resize listener
    const extendedGrid = gridRef.value as ExtendedAgGridVue;
    if (extendedGrid?._resizeCleanup) {
        extendedGrid._resizeCleanup();
    }
};

const exportToCSV = () => {
    gridRef.value?.api?.exportDataAsCsv({ fileName: `${props.title.replace(/\s+/g, '_')}.csv` });
};
</script>

<style scoped lang="scss">
section {
    padding-top: 3rem;
    width: 100%;
}

.error {
    color: red;
    font-size: 14px;
    margin: 10px 0;
}

.operations-insights-grid-section {
    width: 100%;
    margin-bottom: 2rem;
}

.grid-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}
</style>

<style lang="scss"> // Unscoped
.grid-container {
    max-width: unset !important;
}
</style>
